import { manualOrderRequiredChannels } from '@/constants';
import {
  AmiliHealthOrderStatus,
  Currency,
  KitStatus,
  OrderStatus,
} from '@/enums';
import { ChannelName } from '@/enums/channel';
import {
  KitEntity,
  KitStatusHistoryEntity,
} from '@/pages/privatePages/kit/types';
import { ProductEntity } from '@/pages/privatePages/product/types';
import { ProductGroupEntity } from '@/pages/privatePages/productgroup/types';
import { ChangeEvent } from 'react';
import z from 'zod';
import { orderItemSchema, rhfOrderSchema } from './Form/order.schema';

export const ORDER_STATUS_COLOR = {
  [OrderStatus.PENDING]: 'var(--link)',
  [OrderStatus.CONFIRMED]: 'var(--success)',
  [AmiliHealthOrderStatus.CREATED]: 'var(--secondary)',
  [AmiliHealthOrderStatus.COMPLETED]: 'var(--success)',
  [AmiliHealthOrderStatus.PROCESSED]: 'var(--warning)',
  [AmiliHealthOrderStatus.DELIVERED]: 'var(--link)',
  [AmiliHealthOrderStatus.CANCELLED]: 'var(--error)',
  [AmiliHealthOrderStatus.RECEIVED]: 'var(--primary)',
};

export const getScrollY = (
  props: { hasTabs?: boolean; height?: number; more?: number } = {},
) => {
  const { hasTabs, height = 0, more = 0 } = props;
  const DEFAULT_PAGE_HEIGHT = 223;
  const TABS_HEIGHT = 40;
  let pageHeight = DEFAULT_PAGE_HEIGHT + height;
  if (hasTabs) pageHeight += TABS_HEIGHT + 1;
  const header = document.querySelector('.ant-table-header');
  const headerHeight = header?.getBoundingClientRect().height || 40;

  return `calc(100vh - (${pageHeight}px + ${headerHeight}px)${
    ' + ' + more + 'px' || ''
  })`;
};

export const checkKitClinicReceived = (
  statusHistory: KitStatusHistoryEntity[] = [],
) => {
  return statusHistory.some(
    history => history.status === KitStatus.CLINIC_RECEIVED,
  );
};

export const checkAllKitsClinicReceived = (kits: KitEntity[]) => {
  return !kits.some(kit => !checkKitClinicReceived(kit.statusHistory));
};

export const saveOrderToDraft = (
  values: Partial<z.infer<typeof rhfOrderSchema>>,
  key?: string,
) => {
  if (!key) return;
  const draft = sessionStorage.getItem(key);
  const stringifiedValues = JSON.stringify(values);

  if (draft === stringifiedValues) return;
  sessionStorage.setItem(key, stringifiedValues);
};

export const checkManualOrderRequiredChannel = (channelName?: string) => {
  if (!channelName) return false;
  return manualOrderRequiredChannels.includes(channelName as ChannelName);
};

export const calculateProductGroupPrice = (
  productGroupData: ProductGroupEntity[] | undefined,
  orderItems: z.infer<typeof orderItemSchema>[],
  formItem: z.infer<typeof orderItemSchema>,
) => {
  const productId = formItem.productId;

  const group = productGroupData?.find(group =>
    group.productIds.includes(productId),
  );

  if (!group) return formItem.productPrice ?? 0;

  const itemsInGroup = orderItems.filter(item =>
    group.productIds.includes(item?.productId || ''),
  );

  const totalQuantity = itemsInGroup.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0,
  );

  return (
    group.priceRange?.find(
      range =>
        range.quantityFrom <= totalQuantity &&
        (range.quantityTo ?? Infinity) >= totalQuantity,
    )?.price ??
    formItem.productPrice ??
    0
  );
};

// Real per-currency tiered pricing (Product feature's `currencyPricings`) -
// takes priority over the legacy `ProductGroupEntity.priceRange` model in
// `calculateProductGroupPrice` above whenever a product has it. Picks the
// pricing entry matching the order's own project currency, then the
// highest `minQuantity` tier at or below the current quantity (the API
// always folds the base "from 1" price in as its own `minQuantity: 1`
// tier - see buildPricingPayload/buildPricingRanges in the Product
// feature - so there's always a tier to fall back to as long as one
// exists for this quantity).
export const calculateTieredUnitPrice = (
  product: Pick<ProductEntity, 'currencyPricings'> | undefined,
  quantity: number,
  currency: Currency | undefined,
): number | undefined => {
  if (!currency) return undefined;

  const pricing = product?.currencyPricings?.find(
    entry => entry.currency === currency,
  );

  if (!pricing?.tieredPrices?.length) return undefined;

  // Decimal columns can arrive as strings (e.g. "9.00") - Number() before
  // comparing/returning.
  const sortedTiers = [...pricing.tieredPrices].sort(
    (a, b) => Number(a.minQuantity) - Number(b.minQuantity),
  );

  const applicableTier = [...sortedTiers]
    .reverse()
    .find(tier => Number(tier.minQuantity) <= quantity);

  const price = (applicableTier ?? sortedTiers[0])?.price;
  return price != null ? Number(price) : undefined;
};

/**
 * Product-group tiered pricing.
 *
 * Products that share a `productGroupId` are priced together: their order
 * quantities are pooled, and EVERY item of the group is priced off the
 * tiered pricing of the group's FIRST-SELECTED product (the "leader"), at
 * the pooled quantity - each member's own tiers are ignored while it's
 * grouped.
 *
 * Example - A: 90$/ea, 80$/ea from 10 units; B: 100$/ea, 95$/ea from 20
 * units; A and B share a group and A was selected first. Ordering 9 A + 1 B
 * pools to 10 units -> A's tier for 10 applies to both -> 80$ x 10 = 800$
 * (not 9 x 90 + 1 x 100).
 *
 * "First selected" = the group item with the earliest `productSelectedAt`
 * (stamped when a row's product is picked). Rows without a stamp (e.g.
 * items prefilled from an existing order being edited) count as selected
 * before any newly-picked row, in row order. If the leader row is removed
 * or switched to another product, the next-earliest group member becomes
 * the leader automatically.
 *
 * Products without a group (or whose leader has no pricing in the order's
 * currency) fall back to plain per-item tiered pricing at the item's own
 * quantity. Returns `undefined` when no tiered price applies, so callers can
 * fall back further (legacy product-group price range / flat price).
 */
export const calculateGroupedTieredUnitPrice = ({
  item,
  products,
  currency,
  orderItems,
}: {
  currency: Currency | undefined;
  products: ProductEntity[];
  item: z.infer<typeof orderItemSchema>;
  orderItems: z.infer<typeof orderItemSchema>[];
}): number | undefined => {
  const findProduct = (productId?: string | null) =>
    products.find(candidate => candidate.id === productId);

  const product = findProduct(item.productId);
  const productGroupId = product?.productGroupId;

  if (!productGroupId) {
    return calculateTieredUnitPrice(product, Number(item.quantity), currency);
  }

  const groupItems = orderItems
    .map((orderItem, rowIndex) => ({ orderItem, rowIndex }))
    .filter(
      ({ orderItem }) =>
        findProduct(orderItem?.productId)?.productGroupId === productGroupId,
    );

  const pooledQuantity = groupItems.reduce(
    (total, { orderItem }) => total + Number(orderItem.quantity || 0),
    0,
  );

  const [leader] = [...groupItems].sort(
    (a, b) =>
      (a.orderItem.productSelectedAt ?? -Infinity) -
        (b.orderItem.productSelectedAt ?? -Infinity) || a.rowIndex - b.rowIndex,
  );

  const leaderProduct = findProduct(leader?.orderItem.productId) ?? product;

  return (
    calculateTieredUnitPrice(leaderProduct, pooledQuantity, currency) ??
    calculateTieredUnitPrice(product, Number(item.quantity), currency)
  );
};

export const handleTypingSerial = (e: ChangeEvent<HTMLInputElement>) => {
  const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);

  const parts = cleaned.match(/.{1,4}/g) ?? [];

  e.target.value = parts.join('-').toUpperCase();
};
